import { Fragment, useState, useEffect } from "react";
import { XIcon, PlusIcon, MinusIcon } from "@heroicons/react/solid";
import {
  ArrowNarrowRightIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowLeftIcon,
} from "@heroicons/react/outline";
import { Dialog, Menu, Transition } from "@headlessui/react";
import Image from "next/image";
import Head from "next/head";
import { getFullName, findCountryByCode, getUserLanguage } from "utils";
import LoggedPublicNav from "components/LoggedPublicNav/LoggedPublicNav";
import Link from "next/link";
import React, { useRef } from "react";
import useDraggableScroll from "use-draggable-scroll";
import Script from "next/script";
import StepRangeSlider from "libs/react-step-range-slider/StepRangeSlider";
import { useRouter } from "next/router";
import useLocalStorage from "hooks/useLocalStorage";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { Protest_Strike } from "next/font/google";
import Loader from "components/Loader/Loader";
import { Link as ScrollLink } from "react-scroll";
import { LANGUAGES } from "hooks/useLanguage";
import PricingItemCheckIcon from "components/Icons/PricingItemCheckIcon/PricingItemCheckIcon";
import LandingStarIcon from "components/Icons/LandingStarIcon/LandingStarIcon";
import BusinessFooterSocialIcons from "components/BusinessFooterSocialIcons/BusinessFooterSocialIcons";
// import { useQuery } from "@tanstack/react-query";
// import { api } from "utils/api";
const timeRange = [
  { value: 1, step: 1 }, // acts as min value
  { value: 10, step: 5 },
  { value: 100, step: 100 },
  { value: 1000 }, // acts as max value
];
const currencyRange = [
  { value: 5, step: 5 }, // acts as min value
  { value: 100, step: 10 },
  { value: 200, step: 50 },
  { value: 500, step: 100 },
  { value: 1000 }, // acts as max value
];
const landingAvatarSources = [
  "/img/avatars-new/1747897285_avatar%201.png",
  "/img/avatars-new/1745380229_avatar-2.png",
  "/img/avatars-new/1724534309_avatar-3.png",
  "/img/avatars-new/1734642989_avatar-4.png",
  "/img/avatars-new/1765414953_avatar-5.png",
];
const bookDemoUrl =
  "https://docs.google.com/forms/d/e/1FAIpQLSd1fg3tGdl-t0qng77NG3Nb02lTqmBKp9AFWjlNpOLcEui80Q/viewform?usp=publish-editor";
const communityUrl = "https://waylight.me/waylight/community/1039a-ba723";

const LandingAvatarStack = () => (
  <div className="flex items-center justify-center -space-x-2">
    {landingAvatarSources.map((src, index) => (
      <img
        key={src}
        src={src}
        alt=""
        aria-hidden="true"
        className="relative block h-7 w-7 shrink-0 object-contain"
        style={{ zIndex: 10 - index }}
        loading="eager"
        draggable={false}
      />
    ))}
  </div>
);

const getResponsiveWhyIconStyle = (width, height) => ({
  width: `clamp(${width}px, ${(width / 390) * 100}vw, ${Math.round(
    width * 1.12
  )}px)`,
  height: `clamp(${height}px, ${(height / 390) * 100}vw, ${Math.round(
    height * 1.12
  )}px)`,
});

const protestStrike = Protest_Strike({
  subsets: ["latin"],
  weight: "400",
  display: "swap",
  variable: "--font-protest",
});

export const BusinessPage = ({
  user,
  acceptLanguageHeader,
  lang,
  subscriptionOffer,
  businessTitle,
  businessDescription,
  ogImage,
  twitterImage,
}) => {
  const formatCommissionLabel = (
    commission?: number | string,
    additionalCommission?: string
  ) =>
    `${commission ?? ""}%${
      additionalCommission?.trim() ? additionalCommission : ""
    }`;

  const [showPopupVideo, setShowPopupVideo] = useState(false);
  const [clientNumber, setClientNumber] = useState(0);
  const [chargeVideo, setChargeVideo] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);
  const isLogged = user?.id;
  const [isMonthlyBilling, setIsMonthlyBilling] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isProductOpen] = useState(false);
  const [isMobileLanguageMenuOpen, setIsMobileLanguageMenuOpen] =
    useState(false);
  const [activeReviewIndex, setActiveReviewIndex] = useState<number | null>(
    null
  );
  const [currentPlanPrices, setCurrentPlanPrices] = useState({
    subscription_plans: [
      {
        id: "792ab243-5041-4aa7-b433-c74e55d0ff15",
        name: "free",
        price: 0,
        duration: 0,
        type: "cashback",
        currency: "usd",
        comission: 5.0,
        additional_commission: "",
      },
      {
        id: "4eebae8e-4176-403b-9535-91a4ec2d413a",
        name: "basic",
        price: 15,
        duration: 30,
        type: "cashback",
        currency: "usd",
        original_price: 15,
        waylight_discount: 0.0,
        arp_discount: 0.0,
        additional_commission: "",
      },
      {
        id: "7140c24f-3a42-42ba-a93a-d6df493239ef",
        name: "Basic_Yearly",
        price: 9,
        duration: 365,
        gateway: "stripe",
        currency: "usd",
        original_price: 15,
        waylight_discount: 0.0,
        arp_discount: 0.0,
        comission: 0.0,
        additional_commission: "",
      },
      {
        id: "basic-yearly-fallback",
        name: "Basic_Yearly",
        price: 9,
        duration: 365,
        type: "cashback",
        currency: "usd",
        original_price: 15,
        waylight_discount: 40.0,
        arp_discount: 0.0,
      },
      {
        id: "d16a1cb1-7db0-4b53-9c03-e3c404d649d7",
        name: "pro",
        price: 99,
        duration: 30,
        type: "cashback",
        currency: "usd",
        original_price: 99.0,
        waylight_discount: 0.0,
        arp_discount: 0.0,
        additional_commission: "",
      },
      {
        id: "69b12ba8-2196-4d6b-8450-c5fab68072b0",
        name: "pro",
        price: 59,
        duration: 365,
        type: "cashback",
        currency: "usd",
        original_price: 99.0,
        waylight_discount: 40.4,
        arp_discount: 0.0,
        additional_commission: "",
      },
    ],
    type: "cashback",
  });
  const [isLoadingPrice, setLoadingPrice] = useState(true);
  const { t, i18n } = useTranslation();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // const [isExpertFromUA, setIsExpertFromUA] = useState(
  //   UKRAINIAN_TIMEZONES.includes(timeZone) || acceptLanguageHeader === "uk"
  // );
  const [isExpertFromUA, setIsExpertFromUA] = useState(false);
  const [, setR] = useLocalStorage(`r`, null);
  const [, setPlan] = useLocalStorage(`plan`, null);

  const changeLanguageNext = (newLanguage) => {
    if (i18n.language !== newLanguage) {
      i18n.changeLanguage(newLanguage);
      Cookies.set("language", i18n.language);
    }
  };

  const faqItems = [
    { question: t("landingFaqQ1"), answer: t("landingFaqA1") },
    { question: t("landingFaqQ2"), answer: t("landingFaqA2") },
    { question: t("landingFaqQ3"), answer: t("landingFaqA3") },
    { question: t("landingFaqQ4"), answer: t("landingFaqA4") },
    { question: t("landingFaqQ5"), answer: t("landingFaqA5") },
    { question: t("landingFaqQ6"), answer: t("landingFaqA6") },
  ];
  const canonicalUrl = "https://waylight.me";
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waylight",
    url: canonicalUrl,
    logo: `${canonicalUrl}/img/logo.png`,
    description: t("businessDescriptionAll"),
    email: "info@waylight.me",
    address: {
      "@type": "PostalAddress",
      streetAddress: "7209 Lancaster Pike, Suite 4-1282",
      addressLocality: "Hockessin",
      addressRegion: "DE",
      postalCode: "19707",
      addressCountry: "US",
    },
    sameAs: [
      "https://instagram.com/waylightme",
      "https://www.linkedin.com/company/waylightme",
      "https://youtube.com/channel/UCgJWe3BMMsPWHeD8uIHZXaA",
      "https://www.facebook.com/waylightme/",
      "https://t.me/waylightme",
    ],
  };

  const handleImagesLoad = () => {
    setImagesLoaded(true);
  };

  const handleMonthlyClick = () => {
    setIsMonthlyBilling(true);
  };

  const handleAnnuallyClick = () => {
    setIsMonthlyBilling(false);
  };

  useEffect(() => {
    if (Cookies.get("language")) {
      const newLanguage = lang === "uk" ? "ua" : Cookies.get("language");
      changeLanguageNext(newLanguage);
    }
  }, [Cookies.get("language")]);

  useEffect(() => {
    if (!Cookies.get("language")) {
      changeLanguageNext(getUserLanguage(timeZone, acceptLanguageHeader));
    }
  }, []);

  const carouselCards = [
    {
      title: t("landingForWhomEventsTitle"),
      subtitle: t("landingForWhomEventsSubtitle"),
      image: "/img/gallery-landing/creators-gallery-1.svg",
    },
    {
      title: t("landingForWhomCommunitiesTitle"),
      subtitle: t("landingForWhomCommunitiesSubtitle"),
      image: "/img/gallery-landing/creators-gallery-2.svg",
    },
    {
      title: t("landingForWhomCreatorsTitle"),
      subtitle: t("landingForWhomCreatorsSubtitle"),
      image: "/img/gallery-landing/creators-gallery-3.svg",
    },
    {
      title: t("landingForWhomInfluencersTitle"),
      subtitle: t("landingForWhomInfluencersSubtitle"),
      image: "/img/gallery-landing/creators-gallery-4.svg",
    },
    {
      title: t("landingForWhomExpertsTitle"),
      subtitle: t("landingForWhomExpertsSubtitle"),
      image: "/img/gallery-landing/creators-gallery-5.svg",
    },
    {
      title: t("landingForWhomCoachesTitle"),
      subtitle: t("landingForWhomCoachesSubtitle"),
      image: "/img/gallery-landing/creators-gallery-6.svg",
    },
  ];
  const reviewCards = [
    {
      image: "/videos-landing/Video_1.jpg",
      handle: "khomytskyi_official",
      video: "/videos-landing/Video_1.mp4",
    },
    {
      image: "/videos-landing/Video_2.jpg",
      handle: "waylightme",
      video: "/videos-landing/Video_2.mp4",
    },
    {
      image: "/videos-landing/Video_3.jpg",
      handle: "waylightme",
      video: "/videos-landing/Video_3.mp4",
    },
    {
      image: "/videos-landing/Video_4.jpg",
      handle: "waylightme",
      video: "/videos-landing/Video_4.mp4",
    },
    {
      image: "/videos-landing/Video_5.jpg",
      handle: "waylightme",
      video: "/videos-landing/Video_5.mp4",
    },
    {
      image: "/videos-landing/Video_6.jpg",
      handle: "waylightme",
      video: "/videos-landing/Video_6.mp4",
    },
  ];
  const whyChooseItems = [
    {
      icon: "/img/icon-instant-checkout.svg",
      mobileIcon: "/img/why_choose_waylight/1.png",
      mobileIconWidth: 64,
      mobileIconHeight: 64,
      title: t("landingWhyItem1Title"),
      description: t("landingWhyItem1Desc"),
    },
    {
      icon: "/img/icon-higher-conversion.svg",
      mobileIcon: "/img/why_choose_waylight/2.png",
      mobileIconWidth: 64,
      mobileIconHeight: 64,
      title: t("landingWhyItem2Title"),
      description: t("landingWhyItem2Desc"),
    },
    {
      icon: "/img/icon-one-click.svg",
      mobileIcon: "/img/why_choose_waylight/3.png",
      mobileIconWidth: 64,
      mobileIconHeight: 64,
      title: t("landingWhyItem3Title"),
      description: t("landingWhyItem3Desc"),
    },
    {
      icon: "/img/icon-no-download.svg",
      mobileIcon: "/img/why_choose_waylight/4.png",
      mobileIconWidth: 64,
      mobileIconHeight: 64,
      title: t("landingWhyItem4Title"),
      description: t("landingWhyItem4Desc"),
    },
    {
      icon: "/img/icon-global-payments.svg",
      mobileIcon: "/img/why_choose_waylight/5.png",
      mobileIconWidth: 72,
      mobileIconHeight: 64,
      title: t("landingWhyItem5Title"),
      description: t("landingWhyItem5Desc"),
    },
    {
      icon: "/img/icon-lower-fees.svg",
      mobileIcon: "/img/why_choose_waylight/6.png",
      mobileIconWidth: 67,
      mobileIconHeight: 64,
      title: t("landingWhyItem6Title"),
      description: t("landingWhyItem6Desc"),
    },
  ];

  const carouselRef = useRef(null);
  const reviewCarouselRef = useRef(null);
  const reviewVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const { onMouseDown } = useDraggableScroll(carouselRef, {
    direction: "horizontal",
  });
  const { onMouseDown: onReviewMouseDown } = useDraggableScroll(
    reviewCarouselRef,
    { direction: "horizontal" }
  );

  useEffect(() => {
    setMonthlyIncome(clientNumber * chargeVideo);
  }, [clientNumber, chargeVideo]);

  const fullName = user?.expert
    ? getFullName(
        user.expert.firstName,
        user.expert.lastName,
        user.expert.publicId
      )
    : getFullName(user?.firstName, user?.lastName, user?.localpart);
  const ctaAvatar = user?.expert?.avatar || user?.avatar;
  const ctaName = fullName || t("ctaCreateFreeAccount");
  const heroTitleLine1 = t("landingHeroTitleLine1");
  const heroTitleLine2 = t("landingHeroTitleLine2");
  const heroTitleStartsWithTo = /^to\b/i.test(heroTitleLine2);
  const heroTitleSecondLineWithoutTo = heroTitleStartsWithTo
    ? heroTitleLine2.replace(/^to\s*/i, "")
    : heroTitleLine2;

  const router = useRouter();
  const r = router.query.r;

  const path = !isLogged
    ? `/sign-up${r ? `?r=${r}` : ""}`
    : user?.expert
    ? "/my/dashboard"
    : "/my/purchased";

  const savePath = () => {
    isLogged ? null : Cookies.set("path", path);
  };

  const scrollCarousel = (direction) => {
    const container = carouselRef.current as HTMLDivElement | null;
    if (!container) {
      return;
    }
    const card = container.querySelector(
      "[data-carousel-item]"
    ) as HTMLElement | null;
    const cardWidth = card?.getBoundingClientRect().width ?? 280;
    const gap = 24;
    container.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  const scrollReviewCarousel = (direction) => {
    const container = reviewCarouselRef.current as HTMLDivElement | null;
    if (!container) {
      return;
    }
    const card = container.querySelector(
      "[data-review-item]"
    ) as HTMLElement | null;
    const cardWidth = card?.getBoundingClientRect().width ?? 280;
    const gap = 24;
    container.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  const handleReviewPlayToggle = async (index) => {
    const video = reviewVideoRefs.current[index];
    if (!video) {
      return;
    }
    if (activeReviewIndex === index && !video.paused) {
      video.pause();
      setActiveReviewIndex(null);
      return;
    }
    reviewVideoRefs.current.forEach((item, itemIndex) => {
      if (item && itemIndex !== index) {
        item.pause();
        item.currentTime = 0;
      }
    });
    video.muted = false;
    video.volume = 1;
    try {
      await video.play();
      setActiveReviewIndex(index);
    } catch (error) {
      video.controls = true;
    }
  };

  const handlePurchaseSubscription = (subscriptionPlan) => {
    const redirectUrl = encodeURIComponent("/my/settings/businessBilling");

    if (user && user?.expert !== null) {
      setPlan(null);
      r && setR(r);
      return router.push("/my/settings/businessBilling");
    }

    router.push(
      `/sign-up?redirect=${redirectUrl}&plan=${subscriptionPlan}${
        user && user?.expert === null ? "&create=true" : ""
      }${r ? `&r=${r}` : ""}`
    );
  };

  // const { data: subscriptionOffer, refetch } = useQuery(
  //   ["subscription-offer"],
  //   () =>
  //     api.get(
  //       `/offers/v1/subscription-offer${
  //         !r ? (isExpertFromUA ? "?currency=UAH" : "?currency=USD") : ""
  //       }${
  //         r
  //           ? `?r=${r}`
  //           : user?.expert?.referralCode
  //           ? `&r=${user?.expert?.referralCode}`
  //           : ""
  //       }`
  //     ),
  //   { cacheTime: 0 }
  // );
  // const { data: subscriptionOffer, refetch } = useQuery(
  //   ["subscription-offer"],
  //   () =>
  //     api.get(
  //       `/offers/v1/subscription-offer${!r ? "?currency=USD" : ""}${
  //         r
  //           ? `?r=${r}`
  //           : user?.expert?.referralCode
  //           ? `&r=${user?.expert?.referralCode}`
  //           : ""
  //       }`
  //     ),
  //   { cacheTime: 0 }
  // );

  const subscriptionPlans = currentPlanPrices.subscription_plans ?? [];
  const getPlanName = (planName?: string) => (planName || "").toLowerCase();
  const displayedFreePlan =
    subscriptionPlans.find((plan) => getPlanName(plan.name) === "free") ??
    subscriptionPlans[0];
  const basicMonthlyPlan =
    subscriptionPlans.find(
      (plan) =>
        getPlanName(plan.name) === "basic" && Number(plan.duration || 0) <= 31
    ) ??
    subscriptionPlans.find((plan) => getPlanName(plan.name) === "basic") ??
    subscriptionPlans[1];
  const basicYearlyPlan =
    subscriptionPlans.find(
      (plan) => getPlanName(plan.name) === "basic_yearly"
    ) ??
    subscriptionPlans.find(
      (plan) =>
        getPlanName(plan.name) === "basic" && Number(plan.duration || 0) > 31
    ) ??
    subscriptionPlans.find((plan) => getPlanName(plan.name) === "basic") ??
    subscriptionPlans[4] ??
    basicMonthlyPlan;
  const displayedBasicPlan = isMonthlyBilling
    ? basicMonthlyPlan
    : basicYearlyPlan;
  const proMonthlyPlan =
    subscriptionPlans.find(
      (plan) =>
        getPlanName(plan.name) === "pro" && Number(plan.duration || 0) <= 31
    ) ??
    subscriptionPlans.find((plan) => getPlanName(plan.name) === "pro") ??
    subscriptionPlans[2];
  const proYearlyPlan =
    subscriptionPlans.find((plan) => getPlanName(plan.name) === "pro_yearly") ??
    subscriptionPlans.find(
      (plan) =>
        getPlanName(plan.name) === "pro" && Number(plan.duration || 0) > 31
    ) ??
    subscriptionPlans[3] ??
    proMonthlyPlan;
  const displayedProPlan = isMonthlyBilling ? proMonthlyPlan : proYearlyPlan;
  const currency =
    (
      displayedProPlan?.currency ||
      displayedBasicPlan?.currency ||
      displayedFreePlan?.currency ||
      "usd"
    ).toLowerCase() === "uah"
      ? findCountryByCode("UAH")
      : findCountryByCode("USD");
  const freePlan = displayedFreePlan;
  const proPlan = displayedProPlan;
  const yearlyDiscount =
    (proYearlyPlan?.waylight_discount ?? 0) +
    (proYearlyPlan?.arp_discount ?? 0);
  const yearlyDiscountBadge = Math.round(yearlyDiscount || 40);
  const formatPlanName = (name, fallback) => {
    const value = name || fallback || "";
    if (!value) {
      return "";
    }
    const normalizedValue = value
      .replace(/_yearly$/i, "")
      .replace(/_/g, " ")
      .trim();
    if (normalizedValue.toLowerCase() === "free") {
      return value.toUpperCase();
    }
    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
  };

  // const languageUpper = i18n.language.toUpperCase();

  // useEffect(() => {
  //   setIsExpertFromUA(
  //     user
  //       ? user?.expert?.country === "UA"
  //       : UKRAINIAN_TIMEZONES.includes(timeZone) || languageUpper === "UA"
  //   );
  //   refetch();
  // }, [languageUpper, user, timeZone]);

  // useEffect(() => {
  //   refetch();
  // }, [r, isExpertFromUA, user, timeZone]);

  useEffect(() => {
    if (subscriptionOffer) {
      setCurrentPlanPrices(subscriptionOffer);
      setLoadingPrice(false);
    }
  }, []);

  const isExpert = !!user?.expert;

  return (
    <>
      <div
        className={`${
          isProductOpen
            ? "overflow-hidden h-40"
            : "bg-landing-gray scroll-smooth"
        } font-inter ${protestStrike.variable}`}
      >
        <div className="relative -mt-px" id="page-1">
          <Transition appear show={showPopupVideo}>
            <Dialog
              as="div"
              open={showPopupVideo}
              className="fixed inset-0 z-10 flex items-center justify-center h-screen overflow-y-auto"
              onClose={() => setShowPopupVideo(false)}
            >
              <div className="px-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Dialog.Overlay className="fixed inset-0 bg-slate-900 opacity-80" />
                </Transition.Child>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <div className="relative inline-block my-8 text-left align-middle border-4 border-black transition-all transform">
                    <XIcon
                      className="absolute hidden -right-8 w-4 h-4 text-white cursor-pointer sm:block"
                      onClick={() => setShowPopupVideo(false)}
                    />
                    <iframe
                      className="relative sm:w-168 sm:h-96 lazyload"
                      data-src="https://www.youtube.com/embed/w5zjak_iW94"
                      title={t("landingVideoTitle")}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          <div
            className="relative bg-white bg-no-repeat bg-top bg-cover"
            style={{ backgroundImage: "url('/img/bg-landing.svg')" }}
          >
            {isLogged ? (
              <LoggedPublicNav
                imagesLoaded={imagesLoaded}
                language={user?.language}
                loggedUser={{
                  isLoggedIn: true,
                  avatar: user?.avatar,
                  expertAvatar: user?.expert?.avatar,
                  isExpert: !!user?.expert,
                  fullName,
                }}
              />
            ) : (
              <header className="sticky top-0 z-40 px-4 pt-4">
                <div
                  className={`mx-auto max-w-2xl overflow-hidden md:overflow-visible border border-white/10 bg-gradient-to-r from-[#0f0505] via-[#1a0a0a] to-[#0f0505] shadow-[0_4px_20px_-1px_rgba(0,0,0,0.64)] backdrop-blur ${
                    isMobileLanguageMenuOpen ? "rounded-[28px]" : "rounded-full"
                  }`}
                >
                  <div
                    className={`flex items-center justify-between ${
                      isMobileLanguageMenuOpen
                        ? "px-5 pt-3 pb-2"
                        : "px-5 py-1.5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src="/img/logo/waylight-standart-logo.svg"
                        alt="Waylight logo"
                        className="h-7 md:h-8"
                      />
                    </div>

                    <nav className="hidden md:flex flex-1 items-center justify-center gap-6 text-sm font-medium text-white">
                      <span
                        className="h-6 w-px bg-white/10"
                        aria-hidden="true"
                      />
                      <ScrollLink
                        to="features"
                        smooth={true}
                        duration={500}
                        offset={!imagesLoaded ? 0 : 0}
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavProduct")}
                      </ScrollLink>
                      <a
                        href="#pricing"
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavPricing")}
                      </a>
                      <ScrollLink
                        to="reviews"
                        smooth={true}
                        duration={500}
                        offset={130}
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavReviews")}
                      </ScrollLink>
                      <span
                        className="h-6 w-px bg-white/10"
                        aria-hidden="true"
                      />
                      <Menu as="div" className="relative">
                        {({ open }) => (
                          <>
                            <Menu.Button className="flex items-center gap-2 text-white transition hover:text-white focus:outline-none">
                              {i18n.language.toUpperCase()}
                              <ChevronDownIcon className="h-4 w-4" />
                            </Menu.Button>
                            <Transition
                              show={open}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute -left-[327%] top-[2.5vh] flex h-[479px] w-[343px] flex-col rounded-2xl bg-gradient-to-b from-[#140707] via-[#140707] to-[#0f0505] p-5 text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-white/10 focus:outline-none">
                                <p className="text-sm text-white">
                                  {t("changeLanguage")}
                                </p>
                                <div className="mt-4 min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                                  {LANGUAGES.map((language) => {
                                    const isActive =
                                      i18n.language.toLowerCase() ===
                                      language.code.toLowerCase();
                                    return (
                                      <Menu.Item key={language.id}>
                                        <button
                                          className="flex w-full items-center justify-between rounded-2xl px-2 py-1.5 text-left text-sm text-white/90 transition hover:bg-white/5"
                                          onClick={() => {
                                            changeLanguageNext(
                                              language.code.toLowerCase()
                                            );
                                          }}
                                        >
                                          <span>{language.name}</span>
                                          {isActive && (
                                            <CheckIcon className="h-5 w-5 text-emerald-400" />
                                          )}
                                        </button>
                                      </Menu.Item>
                                    );
                                  })}
                                </div>
                                <div className="mt-4 border-t border-white/10 pt-4">
                                  <Menu.Item>
                                    <button className="w-full rounded-full border border-white/10 bg-white/[0.08] py-2 text-sm font-semibold text-white shadow-[inset_0_0_8px_1px_rgba(255,255,255,0.16)] transition hover:bg-white/[0.12]">
                                      {t("close")}
                                    </button>
                                  </Menu.Item>
                                </div>
                              </Menu.Items>
                            </Transition>
                          </>
                        )}
                      </Menu>
                    </nav>

                    <div className="flex items-center gap-3">
                      <Link
                        href="/sign-in"
                        passHref
                        className="rounded-full border border-white/20 bg-gradient-to-b from-white/15 to-white/5 px-5 py-2 text-sm font-semibold text-white shadow-[inset_0_0_8px_1px_rgba(255,255,255,0.16)] backdrop-blur-[2px] transition hover:from-white/20 hover:to-white/10"
                      >
                        {t("landingNavSignIn")}
                      </Link>
                      <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center md:hidden"
                        aria-label={t("landingNavOpenMenu")}
                        aria-expanded={isMobileLanguageMenuOpen}
                        onClick={() =>
                          setIsMobileLanguageMenuOpen((previous) => !previous)
                        }
                      >
                        <span className="flex flex-col items-center gap-1">
                          <span className="h-0.5 w-5 rounded-full bg-white" />
                          <span className="h-0.5 w-5 rounded-full bg-white" />
                          <span className="h-0.5 w-5 rounded-full bg-white" />
                        </span>
                      </button>
                    </div>
                  </div>
                  {isMobileLanguageMenuOpen && (
                    <div className="md:hidden">
                      <div className="px-5 pt-5 pb-4">
                        <div className="mb-5 border-t border-white/10" />
                        <p className="text-sm text-white/90">
                          {t("changeLanguage")}
                        </p>
                        <div className="mt-3 max-h-[calc(100vh-250px)] space-y-1 overflow-y-auto pr-1">
                          {LANGUAGES.map((language) => {
                            const isActive =
                              i18n.language.toLowerCase() ===
                              language.code.toLowerCase();
                            return (
                              <button
                                key={language.id}
                                className="flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm text-white/90 transition hover:bg-white/5"
                                onClick={() => {
                                  changeLanguageNext(
                                    language.code.toLowerCase()
                                  );
                                  setIsMobileLanguageMenuOpen(false);
                                }}
                              >
                                <span>{language.name}</span>
                                {isActive && (
                                  <CheckIcon className="h-5 w-5 text-emerald-400" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="border-t border-white/10 px-5 py-4">
                        <button
                          className="w-full rounded-full border border-white/10 bg-white/10 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
                          onClick={() => setIsMobileLanguageMenuOpen(false)}
                        >
                          {t("close")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </header>
            )}

            <section className="text-slate-900 pt-[40px] md:pt-[75px] pb-16 md:pb-24">
              <div className="flex flex-col items-center text-center w-full max-w-4xl md:max-w-[1065px] mx-auto px-5 relative">
                <LandingAvatarStack />
                <p className="mt-2 text-[12px] leading-[16px] tracking-[0] font-normal text-[#0F0003]">
                  {t("landingTrustedCreators")}
                </p>
                <div className="mt-2 flex items-center justify-center gap-1 text-[#DC6803]">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <LandingStarIcon key={index} className="h-5 w-5" />
                  ))}
                </div>

                <h1
                  className="mt-6 text-[34px] sm:text-[44px] md:text-[64px] font-protest font-normal leading-[1.05]"
                  data-testid="main-heading"
                >
                  <span className="text-[#7F7778]">{heroTitleLine1}</span>
                  <br />
                  {heroTitleStartsWithTo ? (
                    <>
                      <span className="text-[#7F7778]">to </span>
                      {heroTitleSecondLineWithoutTo}
                    </>
                  ) : (
                    heroTitleLine2
                  )}
                </h1>
                <p
                  className="mt-6 text-base max-w-[650px] font-medium font-inter text-black/80
"
                >
                  {t("landingHeroSubtitle")}
                </p>

                <div
                  className="mt-4 flex w-full max-w-2xl flex-col md:flex-row items-center justify-center gap-2 md:gap-4"
                  data-testid="cta-container"
                >
                  {isLogged ? (
                    <a
                      href={path}
                      onClick={savePath}
                      className="group grid h-12 w-full grid-cols-[2rem_minmax(0,1fr)_1rem] items-center rounded-full bg-[#FF2D55] px-6 text-sm font-semibold leading-none text-white shadow-[0_18px_32px_rgba(255,45,85,0.4),0_36px_60px_rgba(255,45,85,0.28)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red focus:outline-none font-inter md:h-[56px] md:w-auto"
                      data-testid="cta-button"
                    >
                      <span className="flex h-8 w-8 self-center rounded-full border border-white/20 bg-white/15 items-center justify-center">
                        {ctaAvatar ? (
                          <img
                            src={ctaAvatar}
                            alt={ctaName}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-white">
                            {ctaName.charAt(0)}
                          </span>
                        )}
                      </span>
                      <span className="self-center truncate px-3 text-center leading-none">
                        {ctaName}
                      </span>
                      <span className="relative flex h-4 w-4 self-center items-center justify-center overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </a>
                  ) : (
                    <a
                      href={path}
                      onClick={savePath}
                      className="group flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-transparent bg-[#FF2D55] px-8 text-base font-medium tracking-[-0.01em] text-white shadow-[0_18px_32px_rgba(255,45,85,0.4),0_36px_60px_rgba(255,45,85,0.28)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red focus:outline-none md:h-[56px] md:w-auto md:px-10"
                      data-testid="cta-button"
                    >
                      <span>{t("ctaStartSelling")}</span>
                      <span className="relative h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </a>
                  )}
                  <a
                    href={bookDemoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-12 w-full items-center justify-center rounded-full border border-white bg-white/40 px-8 py-0 text-base font-medium text-black font-inter md:h-[56px] md:w-auto md:border md:border-white md:py-0"
                  >
                    {t("ctaBookDemo")}
                  </a>
                </div>

                <p
                  className="mt-4 text-sm text-[#0F0003A3]
font-inter"
                >
                  {t("ctaNoCardCancel")}
                </p>

                <div className="mt-12 w-full max-w-sm md:hidden">
                  <Image
                    src="/img/hero-mobile-events.png"
                    alt={t("landingHeroSubtitle")}
                    width={600}
                    height={500}
                    layout="responsive"
                    sizes="100vw"
                    quality={100}
                    priority
                  />
                </div>
                <div className="mt-10 hidden w-full max-w-[1065px] md:block">
                  <Image
                    src="/img/hero.png"
                    alt={t("landingHeroSubtitle")}
                    width={1065}
                    height={800}
                    layout="responsive"
                    quality={100}
                    loading="eager"
                  />
                </div>
              </div>
            </section>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 -bottom-px h-28 md:h-36 bg-gradient-to-b from-transparent to-landing-gray"
            />
          </div>

          <section
            id="features"
            className="relative md:px-8 pt-8 pb-8 md:pb-24"
          >
            <div className="relative mx-auto max-w-[1200px]">
              <div className="relative z-10 rounded-[40px] md:rounded-[200px] bg-white md:shadow-[0_24px_60px_rgba(0,0,0,0.08)] overflow-hidden">
                <div className="absolute inset-0 rounded-[56px] md:rounded-[200px] shadow-[inset_0_18px_0_rgba(113,147,0,0.32)] md:shadow-[inset_0_24px_0_rgba(113,147,0,0.32)] pointer-events-none" />
                <div className="relative px-6 py-10 md:px-16 md:py-16">
                  <div className="mx-auto max-w-2xl text-center">
                    <h2 className="text-[24px] md:text-[48px] font-normal leading-tight font-protest">
                      {t("landingBioTitle")}
                    </h2>
                  </div>
                  <p className="mx-auto mt-4 w-full max-w-[1072px] text-center text-sm md:text-base text-black/80 font-inter">
                    {t("landingBioSubtitle")
                      .split("\n")
                      .map((line, index) => (
                        <span key={index} className="block">
                          {line}
                        </span>
                      ))}
                  </p>

                  <div className="mt-8 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 text-left font-inter">
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardCommunitiesTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardCommunitiesDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardEventsTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardEventsDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardStreamsTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardStreamsDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardDigitalTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardDigitalDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardCallsTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardCallsDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)]">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardAppointmentsTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardAppointmentsDesc")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-[rgba(15,0,3,0.16)] bg-white px-4 py-4 shadow-[0_-3px_4px_rgba(113,147,0,0.25)] md:col-start-2 md:col-span-1">
                      <h3 className="font-semibold text-black">
                        {t("landingBioCardTipsTitle")}
                      </h3>
                      <p className="mt-1 text-sm md:text-base text-black/80">
                        {t("landingBioCardTipsDesc")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-24 md:h-32 bg-gradient-to-b from-transparent via-white/70 to-white"
            />
          </section>

          <section className="bg-white px-5 pb-16 md:px-5 md:pb-24">
            <div className="mx-auto max-w-6xl text-center">
              <img
                src="/img/earth.svg"
                alt=""
                aria-hidden="true"
                className="mx-auto h-20 w-20"
              />
              <h2 className="mt-5 text-[28px] md:text-[56px] max-w-2xl mx-auto font-normal leading-tight text-[#0F0003] font-protest">
                {t("landingTrustedCreators")}
              </h2>
              <p className="mt-4 whitespace-pre-line text-sm md:text-base text-black/80 font-inter max-w-[350px] md:max-w-[920px] mx-auto">
                {t("landingStatsSubtitle")}
              </p>
            </div>

            <div className="mx-auto mt-14 grid grid-cols-1 gap-6 md:gap-4 max-w-6xl xl:max-w-[1184px] lg:grid-cols-[1.25fr_1fr_1fr] xl:grid-cols-[528px_320px_320px]">
              <div className="relative mx-auto h-[400px] w-full max-w-[390px] md:w-[343px] md:max-w-full lg:mx-0 lg:h-[516px] lg:w-full rounded-[28px] lg:rounded-[40px] bg-[#0F172A] text-white overflow-hidden">
                <img
                  src="/img/card-nose-1.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <div className="relative z-10 flex h-full flex-col items-start justify-between p-6 md:p-8">
                  <img
                    src="/img/PolySphere.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-[100px] w-[100.37px] opacity-90"
                  />
                  <div className="w-full text-right">
                    <p className="font-protest text-[56px] leading-[64px] md:text-[72px] md:leading-[88px] lg:text-[96px] lg:leading-[130px] tracking-[-1px] font-semibold">
                      {t("landingStatsUsersValue")}
                    </p>
                    <p className="font-protest font-medium text-sm leading-5 md:text-[32px] md:leading-[28px] text-[rgba(255,255,255,0.64)]">
                      {t("landingStatsUsersServed")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative mx-auto h-[400px] w-full max-w-[390px] md:w-[343px] md:max-w-full lg:mx-0 lg:h-[516px] lg:w-full rounded-[28px] lg:rounded-[40px] bg-[#e9e9ea] text-slate-900 overflow-hidden">
                <img
                  src="/img/card-nose-2.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <div className="relative z-10 flex h-full flex-col items-start justify-between p-6 md:p-8">
                  <img
                    src="/img/RingGrid.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-[100px] w-[112.24px] opacity-80"
                  />
                  <div className="w-full text-right">
                    <p className="font-protest font-semibold text-[56px] leading-[64px] md:text-[72px] md:leading-[88px] lg:text-[56px] lg:leading-[64px] text-[#0F172A]">
                      {t("landingStatsTransactionsValue")}
                    </p>
                    <p className="font-protest font-medium text-sm leading-5 md:text-[20px] md:leading-[28px] text-[rgba(15,23,42,0.64)]">
                      {t("landingStatsTransactionsMonthly")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative mx-auto h-[400px] w-full max-w-[390px] md:w-[343px] md:max-w-full lg:mx-0 lg:h-[516px] lg:w-full rounded-[28px] lg:rounded-[40px] bg-[#e9e9ea] text-slate-900 overflow-hidden">
                <img
                  src="/img/card-nose-2.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <div className="relative z-10 flex h-full flex-col items-start justify-between p-6 md:p-8">
                  <img
                    src="/img/WarpedSphere.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-[100px] w-[100px] opacity-80"
                  />
                  <div className="w-full text-right">
                    <p className="font-protest font-semibold text-[56px] leading-[64px] md:text-[72px] md:leading-[88px] lg:text-[56px] lg:leading-[64px] text-[#0F172A]">
                      {t("landingStatsRevenueValue")}
                    </p>
                    <p className="font-protest font-medium text-sm leading-5 md:text-[20px] md:leading-[28px] text-[rgba(15,23,42,0.64)]">
                      {t("landingStatsAnnualEarnings")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div
            className="relative bg-white"
            style={{
              backgroundImage: "url('/img/bg-low.svg')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center 40%",
              backgroundSize: "100% auto",
            }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 z-0 h-16 md:h-24 bg-gradient-to-b from-white via-white/85 to-transparent"
            />
            <section
              className="relative z-10 text-slate-900"
              data-testid="business-calculator-section"
            >
              <div className="mx-auto max-w-6xl px-2 md:px-5">
                <div className="flex flex-col items-center">
                  <h2
                    className="text-center text-[22px] md:text-[48px] font-normal text-black font-protest"
                    data-testid="how-it-works-section"
                  >
                    {t("howItWorks")}?
                  </h2>
                  <div className="mt-8 flex w-full justify-center">
                    <img
                      src="/img/StepsSection.svg"
                      alt={`${t("howItWorks")} — ${t("landingHeroSubtitle")}`}
                      className="hidden w-full max-w-5xl md:block"
                    />
                    <img
                      src="/img/StepsSection-mobile.svg"
                      alt={`${t("howItWorks")} — ${t("landingHeroSubtitle")}`}
                      className="w-full max-w-sm md:hidden"
                    />
                  </div>
                </div>

                <div className="mt-12 md:mt-24 text-center">
                  <h2
                    className="text-[28px] md:text-[36px] tracking-tight font-protest font-normal whitespace-pre-line"
                    data-testid="business-calculator-title"
                  >
                    {t("businessCalculatorNew")}
                  </h2>
                  <p
                    className="mt-3 text-sm md:text-base text-slate-600 font-inter"
                    data-testid="business-calculator-description"
                  >
                    {t("howMuchMoneyNew")}
                  </p>
                </div>

                <div className="mt-8 lg:mt-12">
                  <div
                    className="text-slate-900 overflow-hidden z-30 rounded-[32px] border-4 border-white/80 bg-[#F5F5F5] lg:rounded-[56px] lg:border-0 lg:bg-white lg:shadow-[0_30px_80px_rgba(0,0,0,0.12)]"
                    data-testid="calculator-box"
                  >
                    <div className="flex flex-col gap-4 p-2 lg:p-4 lg:flex-row lg:items-stretch lg:gap-0">
                      <div className="flex-1 rounded-[24px] bg-white px-6 py-8 lg:rounded-none lg:bg-transparent">
                        <div className="pb-14">
                          <h3
                            className="text-slate-900 font-medium lg:text-lg lg:font-protest"
                            data-testid="estimate-number-title"
                          >
                            {t("estimateNumberNew")}
                          </h3>
                          <div className="mt-6" />
                          <div>
                            <StepRangeSlider
                              value={clientNumber}
                              range={timeRange}
                              onChange={(value) => setClientNumber(value)}
                              className="calculator-slider"
                            />
                          </div>
                          <div
                            className="flex justify-between mt-4 text-xs lg:text-sm text-[#6f676b] lg:text-slate-900"
                            data-testid="client-number-scale"
                          >
                            <div className="mx-1">1</div>
                            <div>10</div>
                            <div>50</div>
                            <div>100</div>
                            <div>1000</div>
                          </div>
                        </div>

                        <div className="pt-2">
                          <h3
                            className="text-slate-900 font-medium lg:text-lg lg:font-protest"
                            data-testid="charge-amount-title"
                          >
                            {t("howMuchYouChargeNew")}
                          </h3>
                          <div className="mt-6" />
                          <div>
                            <StepRangeSlider
                              value={chargeVideo}
                              range={currencyRange}
                              onChange={(value) => setChargeVideo(value)}
                              className="calculator-slider"
                            />
                          </div>
                          <div
                            className="flex justify-between mt-4 text-xs lg:text-sm text-[#6f676b] lg:text-slate-900"
                            data-testid="charge-amount-scale"
                          >
                            <div className="mx-1">$5</div>
                            <div>$50</div>
                            <div>$100</div>
                            <div>$200</div>
                            <div>$1000</div>
                          </div>
                        </div>
                      </div>

                      <div
                        className="flex w-full flex-col items-center justify-center gap-4 px-6 pt-2 pb-8 lg:rounded-[44px] lg:rounded-tl-none lg:rounded-bl-none lg:bg-[#f1f0ef] lg:px-6 lg:py-8 lg:ml-10 lg:h-[419px] lg:w-[320px]"
                        data-testid="monthly-income-box"
                      >
                        <div className="text-center">
                          <p
                            className="text-4xl lg:text-6xl leading-none font-protest font-normal"
                            data-testid="monthly-income-amount"
                          >
                            ${monthlyIncome}
                          </p>
                          <p
                            className="mt-2 text-sm font-medium text-slate-700"
                            data-testid="monthly-income-currency"
                          >
                            {t("landingCalculatorPerMonth")}
                          </p>
                        </div>
                        <div className="h-px w-[60%] bg-black/10" />
                        <p className="text-xs lg:text-sm text-slate-700 text-center">
                          {t("landingCalculatorStartSelling")}
                        </p>
                        <a
                          href={path}
                          onClick={savePath}
                          className="group hidden h-[56px] w-full max-w-[240px] items-center justify-center gap-2 rounded-full bg-landing-red px-6 text-base font-semibold text-white shadow-[0_12px_24px_rgba(255,45,85,0.35)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red focus:outline-none lg:flex"
                          data-testid="sign-up-button"
                        >
                          <span data-testid="sign-up-text">
                            {t("ctaExploreNow")}
                          </span>
                          <span className="relative h-4 w-4 overflow-hidden">
                            <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                            <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-center text-xs md:text-base text-black/80">
                  {t("landingCalculatorExample")}
                </p>
              </div>
            </section>

            <section className="relative z-10 px-5 pt-16 md:pt-40">
              <div className="mx-auto max-w-6xl relative">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <h2 className="text-[28px] md:text-[48px] font-normal text-black font-protest">
                      {t("landingForWhomTitle")}
                    </h2>
                    <p className="mt-2 text-sm md:text-base font-normal text-black/80 font-inter">
                      {t("landingForWhomSubtitle")}
                    </p>
                  </div>
                  <div className="hidden md:flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => scrollCarousel(-1)}
                      className="h-11 w-11 rounded-full border border-black/10 bg-white shadow-sm flex items-center justify-center text-slate-900 transition hover:bg-black hover:text-white"
                      aria-label={t("landingCarouselPrev")}
                    >
                      <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollCarousel(1)}
                      className="h-11 w-11 rounded-full border border-black/10 bg-white shadow-sm flex items-center justify-center text-slate-900 transition hover:bg-black hover:text-white"
                      aria-label={t("landingCarouselNext")}
                    >
                      <ArrowRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-8 md:mt-14">
                  <div
                    ref={carouselRef}
                    onMouseDown={onMouseDown}
                    className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4 pr-[28px] md:pr-[52px] lg:pr-[46px] -mr-5 md:-mr-5 xl:-mr-[calc((100vw-72rem)/2)]"
                  >
                    {carouselCards.map((card, index) => (
                      <div
                        key={`${card.title}-${index}`}
                        data-carousel-item
                        className="min-w-[280px] md:min-w-[350px] snap-start"
                      >
                        <div className="relative">
                          <img
                            src={card.image}
                            alt={`${card.title} — ${card.subtitle}`}
                            className="w-full h-auto"
                          />
                          <a
                            href={path}
                            onClick={savePath}
                            className="absolute bottom-[0.2%] left-[58.5%] flex h-[43px] w-[115px] items-center justify-between gap-2 rounded-full border border-white bg-[#ECEBEB] px-2 text-sm font-medium text-black shadow-md md:h-[54px] md:w-[144px] md:px-3"
                          >
                            <span className="min-w-0 flex-1 truncate text-center">
                              {t("landingForWhomCta")}
                            </span>
                            <ArrowUpIcon className="h-4 w-4 shrink-0 rotate-45 md:h-4 md:w-4" />
                          </a>
                        </div>
                        <div className="mt-4">
                          <p className="text-lg md:text-2xl text-black font-protest font-normal">
                            {card.title}
                          </p>
                          <p className="text-sm md:text-base text-black/60 font-medium">
                            {card.subtitle}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-24 md:h-32 bg-gradient-to-b from-transparent via-white/70 to-white"
            />
          </div>

          <section className="bg-white px-5 py-14 md:py-40 md:px-14">
            <div className="mx-auto max-w-6xl text-center">
              <h2 className="text-[24px] md:text-[48px] font-normal text-black font-protest">
                {t("landingWhyTitle")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-black/80 font-inter">
                {t("landingWhySubtitleLine1")}
                <br className="hidden md:block" />
                {t("landingWhySubtitleLine2")}
              </p>

              <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
                {whyChooseItems.map((item, index) => (
                  <div key={`${item.title}-${index}`} className="text-center">
                    <img
                      src={item.mobileIcon}
                      alt=""
                      aria-hidden="true"
                      className="mx-auto object-contain md:hidden"
                      style={getResponsiveWhyIconStyle(
                        item.mobileIconWidth,
                        item.mobileIconHeight
                      )}
                    />
                    <img
                      src={item.icon}
                      alt=""
                      aria-hidden="true"
                      className="mx-auto hidden h-20 w-20 md:block"
                    />
                    <h3 className="mt-4 text-base md:text-xl font-normal leading-snug font-protest">
                      {item.title.split("\n").map((line, lineIndex) => (
                        <span key={lineIndex} className="block">
                          {line}
                        </span>
                      ))}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-black/80 max-w-xs mx-auto">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="relative bg-[#faf2f3] px-5 py-14 lg:py-24">
            <div className="mx-auto max-w-6xl">
              <div className="rounded-[28px] md:rounded-[40px] bg-transparent">
                <div className="relative">
                  <img
                    src="/img/ecosystem-map.png"
                    alt={`${t("landingEcosystemTitle")} — ${t(
                      "landingEcosystemSubtitle"
                    )}`}
                    className="hidden lg:block w-full"
                  />
                  <img
                    src="/img/ecosystem-map-mobile.png"
                    alt={`${t("landingEcosystemTitle")} — ${t(
                      "landingEcosystemSubtitle"
                    )}`}
                    className="lg:hidden w-full"
                  />

                  <div className="hidden lg:flex absolute top-[20%] bottom-0 left-0 right-0 mx-auto w-full max-w-[760px] flex-col items-center justify-center text-center px-6">
                    <h3 className="max-w-[640px] break-words text-[28px] md:text-[32px] leading-[1.1] font-normal text-black font-protest">
                      {t("landingEcosystemTitle")}
                    </h3>
                    <p className="mt-3 max-w-[620px] break-words text-sm md:text-base leading-snug text-black/80">
                      {t("landingEcosystemSubtitle")}
                    </p>
                    <a
                      href={path}
                      onClick={savePath}
                      className="group mt-6 inline-flex items-center justify-center gap-3 rounded-full bg-landing-red px-8 py-3 text-base font-semibold text-white shadow-[inset_0_4px_4px_rgba(255,255,255,0.24),0_12px_24px_rgba(255,45,85,0.35)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red"
                    >
                      {t("landingPricingGetStarted")}
                      <span className="relative h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </a>
                  </div>
                </div>

                <div className="lg:hidden mt-8 text-center">
                  <h3 className="text-[22px] font-normal text-black font-protest">
                    {t("landingEcosystemTitle")}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600">
                    {t("landingEcosystemSubtitle")}
                  </p>
                  <a
                    href={path}
                    onClick={savePath}
                    className="group mt-6 inline-flex h-[56px] items-center justify-center gap-3 rounded-full bg-landing-red px-7 py-0 text-base font-semibold text-white shadow-[inset_0_4px_4px_rgba(255,255,255,0.24),0_12px_24px_rgba(255,45,85,0.35)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red"
                  >
                    {t("landingPricingGetStarted")}
                    <span className="relative h-4 w-4 overflow-hidden">
                      <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                      <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section
            id="pricing"
            className="bg-white px-5 pt-16 md:pt-24"
            data-testid="pricing-section"
          >
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:gap-6">
                <div className="flex flex-col items-center gap-6 lg:col-span-2 lg:items-start">
                  <div className="mx-auto max-w-xl text-center lg:mx-0 lg:text-left">
                    <h2 className="text-[28px] md:text-[48px] font-normal text-black leading-tight font-protest">
                      {t("landingPricingTitleLine1")}
                      <br />
                      {t("landingPricingTitleLine2")}
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-black/80">
                      {t("saveWhenChoosingYearlyPlan")}
                    </p>
                  </div>

                  <div className="flex flex-col items-center lg:hidden">
                    <LandingAvatarStack />
                    <p className="mt-2 text-sm text-[#0F0003]">
                      {t("landingTrustedCreators")}
                    </p>
                    <div className="mt-2 flex items-center gap-1 text-amber-500">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <LandingStarIcon key={index} className="h-5 w-5" />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="size-full flex items-center justify-center">
                      <div className="bg-[rgba(15,0,3,0.04)] content-stretch flex gap-[10px] items-start p-[4px] relative rounded-[12px]">
                        <div
                          aria-hidden="true"
                          className="absolute border border-[rgba(15,0,3,0.04)] border-solid inset-0 pointer-events-none rounded-[12px]"
                        />
                        <button
                          type="button"
                          onClick={handleAnnuallyClick}
                          className={`content-stretch flex gap-[8px] h-[44px] items-center justify-center px-[12px] relative rounded-[12px] shrink-0 transition ${
                            isMonthlyBilling
                              ? ""
                              : "bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)]"
                          }`}
                        >
                          <p className="font-medium leading-[20px] not-italic relative shrink-0 text-[#0f0003] text-[14px] whitespace-nowrap">
                            {t("landingPricingPayYearly")}
                          </p>
                          <div className="bg-[rgba(0,163,65,0.08)] content-stretch flex flex-col items-center justify-center overflow-clip p-[4px] relative rounded-[4px] shrink-0">
                            <p className="font-medium leading-[16px] not-italic relative shrink-0 text-[#00a341] text-[12px] whitespace-nowrap">
                              {t("saveBadge", { percent: yearlyDiscountBadge })}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={handleMonthlyClick}
                          className={`content-stretch flex flex-col h-[44px] items-center justify-center px-[12px] relative rounded-[12px] shrink-0 transition ${
                            isMonthlyBilling
                              ? "bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)]"
                              : ""
                          }`}
                        >
                          <p className="font-medium leading-[20px] not-italic relative shrink-0 text-[#0f0003] text-[14px] whitespace-nowrap">
                            {t("landingPricingPayMonthly")}
                          </p>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex flex-col items-start self-end text-left">
                  <LandingAvatarStack />
                  <p className="mt-2 text-sm text-[#0F0003]">
                    {t("landingTrustedCreators")}
                  </p>
                  <div className="mt-2 flex items-center gap-1 text-amber-500">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <LandingStarIcon key={index} className="h-5 w-5" />
                    ))}
                  </div>
                </div>
              </div>

              {!isLoadingPrice ? (
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-2xl border border-black/10 bg-white p-6">
                    <h3 className="text-2xl font-normal text-black font-protest">
                      {formatPlanName(freePlan?.name, t("freePlan"))}
                    </h3>
                    <p className="mt-2 text-sm text-black/80">
                      {t("startLowestCostsNew")}
                    </p>
                    <div className="mt-4 text-2xl font-normal text-black font-protest">
                      {currency.symbol}
                      {freePlan?.price ?? 0}
                      <span className="font-inter text-sm font-normal text-[#655C5E]">
                        {t("landingPricingPerMonth")}
                      </span>
                    </div>
                    <div className="mt-4 h-px bg-slate-100" />
                    <div className="mt-4 text-xs font-bold uppercase text-black">
                      {t("landingPricingFeaturesLabel")}
                    </div>
                    <p className="mt-2 text-sm text-black/80">
                      {t("landingPricingFreeFeaturesIntro")}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                      {typeof freePlan?.comission === "number" ? (
                        <li className="flex items-start gap-2">
                          <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                          <span>
                            {t("impressionMo", {
                              comission: formatCommissionLabel(
                                freePlan?.comission,
                                freePlan?.additional_commission
                              ),
                            })}
                          </span>
                        </li>
                      ) : null}
                      <li className="flex items-start gap-2">
                        <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                        <span>{t("basicToolsIncluded")}</span>
                      </li>
                      {[
                        t("publishingMarketplace"),
                        t("proToolsIncluded"),
                        t("customizations"),
                        t("verifiedSign"),
                        t("prioritySupport"),
                        t("launchExpert"),
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <XIcon className="mt-0.5 h-5 w-5 text-slate-300" />
                          <span className="line-through text-slate-400">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePurchaseSubscription("Free")}
                      className="mt-6 h-[44px] w-full rounded-full border border-black/10 bg-[#F5F5F5] text-sm font-semibold text-slate-700"
                    >
                      {t("landingPricingGetStarted")}
                    </button>
                    <div className="mt-3 flex items-center justify-center text-xs text-slate-500">
                      {t("landingPricingSeeComplete")}
                      <ArrowNarrowRightIcon className="ml-2 h-4 w-4" />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-black/10 bg-white p-6">
                    <h3 className="text-2xl font-normal text-black font-protest">
                      {formatPlanName(displayedBasicPlan?.name, t("basic"))}
                    </h3>
                    <p className="mt-2 text-sm text-black/80">
                      {t("getEverythingBasicNew")}
                    </p>
                    <div className="mt-4 text-2xl font-normal text-black font-protest">
                      {currency.symbol}
                      {displayedBasicPlan?.price ?? 0}
                      <span className="font-inter text-sm font-normal text-[#655C5E]">
                        {t("landingPricingPerMonth")}{" "}
                        {!isMonthlyBilling && displayedBasicPlan?.duration > 30
                          ? t("landingPricingBilledAnnually")
                          : ""}
                      </span>
                    </div>
                    <div className="mt-4 h-px bg-slate-100" />
                    <div className="mt-4 text-xs font-bold uppercase text-black">
                      {t("landingPricingFeaturesLabel")}
                    </div>
                    <p className="mt-2 text-sm text-black/80">
                      {t("landingPricingBasicFeaturesIntro")}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                      {typeof displayedBasicPlan?.comission === "number" ? (
                        <li className="flex items-start gap-2">
                          <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                          <span>
                            {t("impressionMo", {
                              comission: formatCommissionLabel(
                                displayedBasicPlan?.comission ?? 2,
                                displayedBasicPlan?.additional_commission
                              ),
                            })}
                          </span>
                        </li>
                      ) : null}
                      <li className="flex items-start gap-2">
                        <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                        <span>{t("basicToolsIncluded")}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                        <span>{t("publishingMarketplace")}</span>
                      </li>
                      {[
                        t("proToolsIncluded"),
                        t("customizations"),
                        t("verifiedSign"),
                        t("prioritySupport"),
                        t("launchExpert"),
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <XIcon className="mt-0.5 h-5 w-5 text-slate-300" />
                          <span className="line-through text-slate-400">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() =>
                        handlePurchaseSubscription(
                          isMonthlyBilling ? "Basic" : "basicYear"
                        )
                      }
                      className="mt-6 h-[44px] w-full rounded-full border border-black/10 bg-[#F5F5F5] text-sm font-semibold text-slate-700"
                    >
                      {t("landingPricingGetStarted")}
                    </button>
                    <div className="mt-3 flex items-center justify-center text-xs text-slate-500">
                      {t("landingPricingSeeComplete")}
                      <ArrowNarrowRightIcon className="ml-2 h-4 w-4" />
                    </div>
                  </div>

                  <div className="rounded-2xl border border-landing-red bg-[#fff5f6] p-6 md:col-span-2 md:mx-auto md:w-[calc(50%-0.75rem)] lg:col-span-1 lg:mx-0 lg:w-auto">
                    <div className="flex items-start justify-between">
                      <h3 className="text-2xl font-normal text-black font-protest">
                        {formatPlanName(proPlan?.name, t("pro"))}
                      </h3>
                      <span className="rounded-[4px] border border-[#EBF8F0] bg-[#EBF8F0] px-3 py-1 text-xs font-semibold text-[#00A341]">
                        {t("landingPricingRecommended")}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-black/80">
                      {t("GetMoreSalesNew")}
                    </p>
                    <div className="mt-4 text-2xl font-normal text-black font-protest">
                      {currency.symbol}
                      {proPlan?.price ?? 0}
                      <span className="font-inter text-sm font-normal text-[#655C5E]">
                        {t("landingPricingPerMonth")}{" "}
                        {!isMonthlyBilling && proPlan?.duration > 30
                          ? t("landingPricingBilledAnnually")
                          : ""}
                      </span>
                    </div>
                    <div className="mt-4 h-px bg-slate-100" />
                    <div className="mt-4 text-xs font-bold uppercase text-black">
                      {t("landingPricingFeaturesLabel")}
                    </div>
                    <p className="mt-2 text-sm text-black/80">
                      {t("landingPricingProFeaturesIntro")}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                      {[
                        t("ziroTransactionFees"),
                        t("basicToolsIncluded"),
                        t("publishingMarketplace"),
                        t("proToolsIncluded"),
                        t("customizations"),
                        t("verifiedSign"),
                        t("prioritySupport"),
                        t("launchExpert"),
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <PricingItemCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() =>
                        handlePurchaseSubscription(
                          isMonthlyBilling ? "Pro" : "proYear"
                        )
                      }
                      className="mt-6 h-[44px] w-full rounded-full bg-landing-red text-sm font-semibold text-white shadow-[0_12px_24px_rgba(255,45,85,0.35)]"
                    >
                      {t("landingPricingGetStarted")}
                    </button>
                    <div className="mt-3 flex items-center justify-center text-xs text-slate-500">
                      {t("landingPricingSeeComplete")}
                      <ArrowNarrowRightIcon className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center h-[500px]">
                  <Loader fullScreen={false} />
                </div>
              )}
            </div>
          </section>

          <section className="bg-white px-5 py-14 md:py-40">
            <div className="mx-auto max-w-6xl">
              <div
                id="reviews"
                className="flex items-start justify-between gap-6"
              >
                <div>
                  <h2 className="text-[26px] md:text-[48px] font-normal text-black font-protest">
                    {t("landingReviewTitle")}
                  </h2>
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#0F0003] font-inter">
                    <PricingItemCheckIcon className="h-5 w-5 shrink-0" />
                    {t("landingTrustedCreators")}
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => scrollReviewCarousel(-1)}
                    className="h-11 w-11 rounded-full border border-black/10 bg-white shadow-sm flex items-center justify-center text-slate-900 transition hover:bg-black hover:text-white"
                    aria-label={t("landingCarouselPrev")}
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollReviewCarousel(1)}
                    className="h-11 w-11 rounded-full border border-black/10 bg-white shadow-sm flex items-center justify-center text-slate-900 transition hover:bg-black hover:text-white"
                    aria-label={t("landingCarouselNext")}
                  >
                    <ArrowRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="mt-8 md:mt-14">
                <div
                  ref={reviewCarouselRef}
                  onMouseDown={onReviewMouseDown}
                  className="flex gap-4 md:gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4 pr-[28px] md:pr-[52px] lg:pr-[46px] -mr-5 md:-mr-5 xl:-mr-[calc((100vw-72rem)/2)]"
                >
                  {reviewCards.map((card, index) => (
                    <div
                      key={`${card.handle}-${index}`}
                      data-review-item
                      className="min-w-[280px] md:min-w-[350px] lg:min-w-[350px] snap-start"
                    >
                      <div className="relative overflow-hidden rounded-[13.71px] bg-slate-200 h-[478px] md:h-[600px]">
                        {card.video ? (
                          <video
                            ref={(node) => {
                              reviewVideoRefs.current[index] = node;
                            }}
                            className="landing-review-video h-full w-full object-cover object-bottom"
                            playsInline
                            preload="auto"
                            poster={card.image}
                            controls={activeReviewIndex === index}
                            controlsList="nodownload noplaybackrate"
                            onClick={() => handleReviewPlayToggle(index)}
                            onPlay={() => setActiveReviewIndex(index)}
                            onPause={() => {
                              if (activeReviewIndex === index) {
                                setActiveReviewIndex(null);
                              }
                            }}
                            onEnded={() => {
                              if (activeReviewIndex === index) {
                                setActiveReviewIndex(null);
                              }
                            }}
                          >
                            <source src={card.video} type="video/mp4" />
                          </video>
                        ) : (
                          <img
                            src={card.image}
                            alt={card.handle}
                            className="h-full w-full object-cover object-bottom"
                          />
                        )}
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition ${
                            activeReviewIndex === index
                              ? "opacity-0 pointer-events-none"
                              : "opacity-100"
                          }`}
                        >
                          <img
                            src="/img/play-landing.svg"
                            alt=""
                            className="h-[57px] w-[57px] md:h-[72px] md:w-[72px]"
                          />
                        </span>
                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-black/5" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section
            id="faq"
            className="bg-white px-5 pb-14 md:pb-40 max-w-3xl mx-auto"
            data-testid="questions-section"
          >
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-[26px] md:text-[48px] font-normal text-black font-protest">
                {t("frequentlyAskedQuestions")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-black/80 font-inter">
                {t("landingFaqSupportLine1")}
                <br />
                {t("landingFaqSupportLine2")}
              </p>

              <div className="mt-6 flex flex-wrap justify-center gap-3 font-inter">
                <a
                  href="https://api.whatsapp.com/send/?phone=13024672761"
                  aria-label={t("landingFaqWhatsappAria")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm"
                >
                  <span
                    className="icon-[logos--whatsapp-icon] inline-block h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  {t("landingFaqWhatsapp")}
                  <ArrowUpIcon className="h-4 w-4 rotate-90 text-black/60" />
                </a>
                <a
                  aria-label={t("landingFaqEmailAria")}
                  href="mailto:support@waylight.me"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm"
                >
                  <span
                    className="icon-[garden--email-fill-16] inline-block h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  {t("landingFaqEmail")}
                  <ArrowUpIcon className="h-4 w-4 rotate-90 text-black/60" />
                </a>
                <a
                  href="https://t.me/waylightme"
                  aria-label={t("landingFaqTelegramAria")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm"
                >
                  <img
                    className="h-4 w-4"
                    src="/img/telegram-questions.png"
                    alt=""
                    aria-hidden="true"
                  />
                  {t("landingFaqTelegram")}
                  <ArrowUpIcon className="h-4 w-4 rotate-90 text-black/60" />
                </a>
              </div>
            </div>

            <div className="mx-auto mt-10 max-w-4xl text-left font-inter">
              <div className="divide-y divide-slate-200">
                {faqItems.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div key={item.question} className="py-5">
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                        aria-expanded={isOpen}
                        aria-controls={`landing-faq-answer-${index}`}
                        className="flex w-full items-start justify-between gap-6 text-left"
                      >
                        <span className="text-sm md:text-base font-medium text-black">
                          {item.question}
                        </span>
                        <span className="flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full border border-black/60 text-black/60">
                          {isOpen ? (
                            <MinusIcon className="h-2 w-2" />
                          ) : (
                            <PlusIcon className="h-2 w-2" />
                          )}
                        </span>
                      </button>
                      <div
                        id={`landing-faq-answer-${index}`}
                        aria-hidden={!isOpen}
                        className={`grid overflow-hidden transition-all duration-300 ease-out ${
                          isOpen
                            ? "mt-3 grid-rows-[1fr] opacity-100"
                            : "mt-0 grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-sm text-black/60">{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <footer
            className="bg-[#0b132a] px-5 text-white font-inter"
            data-testid="footer"
          >
            <div className="mx-auto max-w-6xl pt-24 md:pt-40">
              <div className="rounded-[40px] md:rounded-[200px] bg-[radial-gradient(circle_at_top_left,#ffc2d1,transparent_55%),radial-gradient(circle_at_top_right,#d5e2b3,transparent_60%),linear-gradient(120deg,#ffd0db,#f5f2e6,#d7e2b8)] px-6 py-10 mb-14 md:px-16 md:py-16 text-center text-black">
                <div className="flex flex-col items-center gap-2">
                  <LandingAvatarStack />
                  <p className="text-xs md:text-sm text-[#0F0003]">
                    {t("landingTrustedCreators")}
                  </p>
                  <div className="flex items-center gap-1 text-amber-500">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <LandingStarIcon key={index} className="h-4 w-4" />
                    ))}
                  </div>
                </div>
                <h3 className="mt-6 text-[26px] md:text-[56px] font-normal leading-tight font-protest">
                  {t("landingFooterHeadlineLine1")}
                  <br />
                  {t("landingFooterHeadlineLine2")}
                </h3>
                <p className="mt-4 text-sm md:text-base text-slate-700 max-w-[400px] font-medium font-inter mx-auto">
                  {t("landingFooterSubtitle")}
                </p>
                <div className="mt-6 flex w-full flex-col items-center justify-center gap-3 px-1 md:flex-row md:px-0">
                  {isLogged ? (
                    <Link
                      href={path}
                      passHref
                      className="group flex h-12 w-full items-center justify-between gap-3 overflow-hidden rounded-full bg-landing-red px-4 text-sm font-semibold text-white shadow-[0_18px_32px_rgba(255,45,85,0.4),0_36px_60px_rgba(255,45,85,0.28)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red md:h-[56px] md:w-auto md:px-6"
                    >
                      <span className="h-9 w-9 rounded-full border border-white/20 bg-white/15 flex items-center justify-center">
                        {ctaAvatar ? (
                          <img
                            src={ctaAvatar}
                            alt={ctaName}
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-white">
                            {ctaName.charAt(0)}
                          </span>
                        )}
                      </span>
                      <span className="truncate max-w-[220px]">{ctaName}</span>
                      <span className="relative ml-2 h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </Link>
                  ) : (
                    <Link
                      href={path}
                      passHref
                      className="group flex h-12 w-full items-center justify-center gap-2 rounded-full bg-landing-red px-6 text-base font-semibold text-white shadow-[0_12px_24px_rgba(255,45,85,0.35)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red md:h-[56px] md:w-auto"
                    >
                      {t("ctaStartForFree")}
                      <span className="relative h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </Link>
                  )}
                  <a
                    href={bookDemoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-12 w-full items-center justify-center rounded-full border border-[#0F0003]/[0.16] bg-[#0F0003]/[0.08] px-8 text-base font-semibold text-black font-inter md:h-[56px] md:w-auto md:border md:border-white"
                  >
                    {t("ctaBookDemo")}
                  </a>
                </div>
                <p className="mt-4 text-sm text-slate-600 font-inter font-medium">
                  {t("ctaNoCardCancel")}
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-14 md:my-32 md:grid-cols-[4.5fr_1fr_1fr] md:gap-20">
                <div>
                  <Link href="/" className="inline-flex items-center">
                    <img
                      width={101}
                      height={29}
                      src="/img/logo/waylight-standart-logo.svg"
                      alt="Waylight logo"
                      className="h-7 md:hidden"
                    />
                    <img
                      width={201}
                      height={48}
                      src="/img/logo/logo-desc.svg"
                      alt="Waylight logo"
                      className="hidden h-10 w-auto md:block"
                    />
                  </Link>
                  <p className="mt-4 text-sm text-white max-w-xs">
                    {t("landingFooterTagline")}
                  </p>
                  {isLogged ? (
                    <Link
                      href={path}
                      passHref
                      className="group mt-6 md:mt-12 inline-flex items-center gap-3 rounded-full bg-landing-red px-6 py-3 text-sm font-semibold text-white transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red"
                    >
                      <span className="h-8 w-8 rounded-full border border-white/20 bg-white/15 flex items-center justify-center">
                        {ctaAvatar ? (
                          <img
                            src={ctaAvatar}
                            alt={ctaName}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-white">
                            {ctaName.charAt(0)}
                          </span>
                        )}
                      </span>
                      <span className="truncate max-w-[200px]">{ctaName}</span>
                      <span className="relative ml-2 h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </Link>
                  ) : (
                    <Link
                      href={path}
                      passHref
                      className="group mt-6 md:mt-12 inline-flex items-center gap-2 rounded-full bg-landing-red px-6 py-3 text-base font-semibold text-white shadow-[0_12px_24px_rgba(255,45,85,0.35)] transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-hover-landing-red md:h-[56px] md:py-0"
                    >
                      {t("ctaStartSellingNow")}
                      <span className="relative h-4 w-4 overflow-hidden">
                        <ArrowNarrowRightIcon className="absolute inset-0 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-5" />
                        <ArrowNarrowRightIcon className="absolute inset-0 -translate-y-5 h-4 w-4 transform-gpu transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover:translate-y-0" />
                      </span>
                    </Link>
                  )}
                  <p className="mt-3 text-xs italic text-slate-400">
                    {t("ctaNoCardCancel")}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase text-white">
                    {t("landingFooterForCreators")}
                  </p>
                  <ul className="mt-4 space-y-3 text-sm text-white/80">
                    {/* TODO: add real links */}
                    <li>
                      <a href="#faq" className="hover:text-white">
                        {t("landingFooterHelpCenter")}
                      </a>
                    </li>
                    <li>
                      <a href="#pricing" className="hover:text-white">
                        {t("pricing")}
                      </a>
                    </li>
                    <li>
                      <a href={communityUrl} className="hover:text-white">
                        {t("landingFooterCommunity")}
                      </a>
                    </li>
                    <li>
                      <a href="#reviews" className="hover:text-white">
                        {t("landingFooterWallOfLove")}
                      </a>
                    </li>
                    <li>
                      <span className="text-white/80">
                        {t("landingFooterAmbassador")}
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase text-white font-inter">
                    {t("landingFooterCompany")}
                  </p>
                  <ul className="mt-4 space-y-3 text-sm text-slate-200">
                    {/* TODO: add real links */}
                    <li>
                      <button
                        type="button"
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        className="bg-transparent p-0 text-left text-inherit transition hover:text-white"
                      >
                        {t("landingFooterAbout")}
                      </button>
                    </li>
                    <li>
                      <a
                        href="/docs/Waylight-TermsOfService.pdf"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-white"
                      >
                        {t("landingFooterTerms")}
                      </a>
                    </li>
                    <li>
                      <a
                        href="/docs/Waylight-PrivacyPolicy.pdf"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-white"
                      >
                        {t("landingFooterPrivacy")}
                      </a>
                    </li>
                    <li className="text-slate-300">
                      {t("landingFooterContactUs")}
                      <br />
                      <a
                        href="mailto:info@waylight.me"
                        className="hover:text-white"
                      >
                        {t("landingFooterContactEmail")}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-10 border-t border-white/10 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6 text-xs text-white">
                <div className="md:py-14">
                  {t("landingFooterCopyright")}
                  <br />
                  {t("landingFooterAddress")}
                </div>
                <div className="mb-14 md:mb-0">
                  <BusinessFooterSocialIcons
                    labels={{
                      instagram: t("instagram"),
                      linkedin: t("socialLinkedIn"),
                      youtube: t("socialYouTube"),
                      facebook: t("socialFacebook"),
                      telegram: t("telegram"),
                    }}
                  />
                </div>
              </div>
            </div>
          </footer>

          <Head>
            <meta charSet="utf-8" />
            <title>{t(businessTitle)}</title>
            <meta name="description" content={t(businessDescription)} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={t(businessTitle)} />
            <meta property="og:description" content={t(businessDescription)} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta name="twitter:title" content={t(businessTitle)} />
            <meta name="twitter:description" content={t(businessDescription)} />
            <meta name="twitter:image" content={twitterImage} />
            <meta name="twitter:card" content="summary_large_image" />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(faqStructuredData),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(organizationStructuredData),
              }}
            />
          </Head>
          {process.env.NODE_ENV === "production" ? (
            <Script
              id="waylightgtm"
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM}');`,
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};
